/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  BankOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  HistoryOutlined,
  StarOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  joinWithDot,
  type TeamHistoryCopy,
  type TeamHistoryView,
} from '@sportninja/common/features/registration/teamHistory'
import Text from 'src/components/ts-components/Text'
import HistoryMetaItem from './HistoryMetaItem'
import SummaryOfficialRow from './SummaryOfficialRow'
import SummaryStatTile from './SummaryStatTile'
import TeamLogo from './TeamLogo'

interface Props {
  team: { name: string; image?: unknown }
  history: TeamHistoryView
  copy: TeamHistoryCopy
}

/** Shown for a figure the payload does not carry, rather than a made-up 0. */
const UNKNOWN_VALUE = '—'

/**
 * The chosen team's track record, beside the pickers: its organization, three
 * headline figures, where it last played, and who ran it. A team that never
 * played gets one sentence saying so instead of empty figures.
 */
const TeamSummaryCard = ({ team, history, copy }: Props) => {
  const { summary } = copy
  const last = history.lastCompetition

  return (
    <section aria-label={`${team.name} details`} css={cardStyle}>
      <div css={headerStyle}>
        <TeamLogo team={team} size={48} />
        <div css={headerTextStyle}>
          <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
            {team.name}
          </Text>
          {history.organizationName ? (
            <HistoryMetaItem
              icon={<BankOutlined />}
              label={history.organizationName}
            />
          ) : null}
        </div>
      </div>

      {history.hasPlayed ? (
        <>
          <div css={statsStyle}>
            <SummaryStatTile
              icon={<TrophyOutlined />}
              label={summary.competitions}
              value={String(history.competitionCount)}
              caption={summary.competitionsCaption}
            />
            <SummaryStatTile
              icon={<TeamOutlined />}
              label={summary.players}
              value={
                history.playerCount === null
                  ? UNKNOWN_VALUE
                  : String(history.playerCount)
              }
              caption={summary.playersCaption}
            />
            <SummaryStatTile
              icon={<CalendarOutlined />}
              label={summary.firstPlayed}
              value={history.firstPlayedLabel || UNKNOWN_VALUE}
              caption={summary.firstPlayedCaption}
            />
            <SummaryStatTile
              icon={<FieldTimeOutlined />}
              label={summary.lastPlayed}
              value={history.lastPlayedLabel || UNKNOWN_VALUE}
              caption={summary.lastPlayedCaption}
            />
          </div>

          {last ? (
            <div css={sectionStyle}>
              <Text
                variant='paragraphMedium'
                weight='medium'
                color={colors.NEUTRAL_50}
              >
                {capitalizeWords(summary.lastCompetition)}
              </Text>
              <div css={lastCompetitionStyle}>
                <span css={iconBoxStyle} aria-hidden='true'>
                  <HistoryOutlined />
                </span>
                <span css={stackStyle}>
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.WHITE}
                  >
                    {last.name}
                  </Text>
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    {joinWithDot(last.dateRangeLabel, last.organizationName)}
                  </Text>
                </span>
              </div>
            </div>
          ) : null}

          {history.officials.length > 0 ? (
            <div css={sectionStyle}>
              <div css={sectionHeaderStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='medium'
                  color={colors.NEUTRAL_50}
                >
                  {capitalizeWords(summary.teamOfficials)}
                </Text>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_80}
                >
                  {summary.teamOfficialsCaption}
                </Text>
              </div>
              <ul css={officialsStyle}>
                {history.officials.map((official) => (
                  <SummaryOfficialRow
                    key={official.id}
                    official={official}
                    treasurerLabel={copy.treasurer}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <div css={emptyStyle}>
          <span css={[iconBoxStyle, emptyIconBoxStyle]} aria-hidden='true'>
            <StarOutlined />
          </span>
          <span css={stackStyle}>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              {capitalizeWords(summary.noHistoryTitle)}
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {summary.noHistoryBody}
            </Text>
          </span>
        </div>
      )}
    </section>
  )
}

export default TeamSummaryCard

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${colors.NEUTRAL_200};
  border-radius: 8px;
  background-color: ${colors.NEUTRAL_700};
`

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const headerTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

/* Two by two. Each row of tiles is three grid rows — label, value, caption —
   shared through `subgrid`, so the figures line up across tiles even when a
   label wraps. The 8px row gap only shows between the two rows of tiles; each
   tile sets its own 4px inside. */
const statsStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, auto auto auto);
  gap: 8px;
`

/* Each block below the figures is ruled off from the one above. */
const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid ${colors.VERY_DARK_GRAYISH_BLUE};
`

const sectionHeaderStyle = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`

const lastCompetitionStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

/* The tenant primary as an icon color on a dark tile, not a fill. */
const iconBoxStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${colors.NEUTRAL_600};
  color: ${colors.PRIMARY};
  font-size: 18px;
`

const emptyIconBoxStyle = css`
  background-color: ${colors.NEUTRAL_500};
`

const stackStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const officialsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`

const emptyStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${colors.NEUTRAL_600};
`
